import { useEffect, useState } from "react";
import { Plus, X, Loader2, MapPin } from "lucide-react";
import { getPosition } from "@/lib/geo";
import { ChartEntry } from "@/components/sites/ChartEntry";
import { api, apiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { UST_DATABASE, PRODUCTS, tankKey, formatGal, endsShort, productName } from "@/lib/tank";

const blank = (i) => ({ label: `T${i + 1}`, capacity: 10000, diameter: 96, ends: "dome", startInches: 0, product: PRODUCTS[i % 4].id });

function TankRow({ tank, i, onChange, onRemove, t }) {
  const [chartOpen, setChartOpen] = useState(false);
  const key = tankKey(tank);
  const sizes = UST_DATABASE.some((x) => tankKey(x) === key) ? UST_DATABASE : [tank, ...UST_DATABASE];
  return (
    <div className="edit-row" data-testid={`edit-tank-${i}`}>
      <div className="edit-row-top">
        <input className="text-input edit-label" data-testid={`edit-tank-label-${i}`} maxLength={40} placeholder={t("Label")}
          value={tank.label} onChange={(e) => onChange({ label: e.target.value })} />
        {onRemove && <button type="button" className="icon-btn icon-btn-sm" data-testid={`edit-tank-remove-${i}`} aria-label={t("Remove tank")} onClick={onRemove}><X size={16} /></button>}
      </div>
      <div className="two-col">
        <select className="text-input edit-select" data-testid={`edit-tank-size-${i}`} value={key}
          onChange={(e) => { const x = sizes.find((s) => tankKey(s) === e.target.value); onChange({ capacity: x.capacity, diameter: x.diameter, ends: x.ends }); }}>
          {sizes.map((x) => <option key={tankKey(x)} value={tankKey(x)}>{formatGal(x.capacity)} gal · {x.diameter}" {t(endsShort(x.ends))}</option>)}
        </select>
        <select className="text-input edit-select" data-testid={`edit-tank-product-${i}`} value={tank.product} onChange={(e) => onChange({ product: e.target.value })}>
          {!PRODUCTS.some((p) => p.id === tank.product) && <option value={tank.product}>{tank.product}</option>}
          {PRODUCTS.map((p) => <option key={p.id} value={p.id}>{t(productName(p.id))}</option>)}
        </select>
      </div>
      <button type="button" className={`load-btn chart-btn ${tank.chart?.length ? "load-btn-active" : ""}`} data-testid={`edit-tank-chart-${i}`} onClick={() => setChartOpen(true)}>
        {tank.chart?.length ? t("Real chart added ({n} rows)", { n: tank.chart.length }) : t("Add the station's real chart")}
      </button>
      {chartOpen && <ChartEntry tank={tank} t={t} onClose={() => setChartOpen(false)} onSave={(rows) => { onChange({ chart: rows }); setChartOpen(false); }} />}
    </div>
  );
}

export function SiteEditor({ site, onCancel, onSaved }) {
  const t = useT();
  const [name, setName] = useState(site?.name || "");
  const [tanks, setTanks] = useState(site?.tanks?.length ? site.tanks.map((x) => ({ ...x })) : [blank(0), blank(1)]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [where, setWhere] = useState(site?.lat != null ? { lat: site.lat, lng: site.lng } : null);
  const [locating, setLocating] = useState(false);
  const locate = async () => { setLocating(true); const p = await getPosition(); setLocating(false); if (p) setWhere(p); else setErr(t("Couldn't get your location. Check that location is allowed for this site.")); };
  useEffect(() => { if (!site) getPosition(6000).then((p) => p && setWhere((w) => w || p)); }, [site]);
  const set = (i, p) => setTanks((all) => all.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const labels = tanks.map((x) => x.label.trim().toLowerCase());
  const dup = labels.some((l, i) => l && labels.indexOf(l) !== i);
  const valid = name.trim() && tanks.length && tanks.every((x) => x.label.trim()) && !dup;

  const save = async () => {
    setBusy(true); setErr("");
    const body = { name: name.trim(), tanks: tanks.map((x) => ({ ...x, label: x.label.trim() })), ...(where || {}) };
    try {
      const saved = site ? await api.updateSite(site.id, body) : await api.addSite(body);
      window.dispatchEvent(new Event("sites-changed"));
      onSaved(saved);
    } catch (e) { setErr(apiError(e) || t("Couldn't save. Check your signal and try again.")); setBusy(false); }
  };

  return (
    <div className="modal-backdrop" data-testid="site-editor" onClick={onCancel}>
      <div className="modal editor-modal" onClick={(e) => e.stopPropagation()}>
        <div className="section-title">{site ? t("Edit site") : t("New site")}</div>
        <input className="text-input" data-testid="edit-site-name" placeholder={t("Station name")} maxLength={80} autoComplete="off"
          value={name} onChange={(e) => setName(e.target.value)} />
        <div className="loc-row" data-testid="edit-location">
          <MapPin size={14} />
          <span className="card-sub" data-testid="edit-location-status">{where ? (site?.lat != null && where.lat === site.lat && where.lng === site.lng ? t("Location saved for this station") : t("Your current location will be saved")) : t("No location yet")}</span>
          <button type="button" className="load-btn" data-testid="edit-use-location" disabled={locating} onClick={locate}>
            {locating ? <Loader2 size={14} className="spin" /> : null}{where ? t("Update") : t("Use my location")}</button>
        </div>
        <div className="edit-list">
          {tanks.map((x, i) => (
            <TankRow key={i} i={i} tank={x} t={t} onChange={(p) => set(i, p)}
              onRemove={tanks.length > 1 ? () => setTanks(tanks.filter((_, j) => j !== i)) : null} />
          ))}
        </div>
        {tanks.length < 12 && (
          <button type="button" className="ghost-btn edit-add" data-testid="edit-add-tank" onClick={() => setTanks([...tanks, blank(tanks.length)])}>
            <Plus size={16} />{t("Add tank")}</button>
        )}
        {dup && <div className="form-error" data-testid="edit-dup-error">{t("Each tank needs its own label.")}</div>}
        {err && <div className="form-error" data-testid="edit-error">{err}</div>}
        <div className="modal-actions">
          <button type="button" className="ghost-btn" data-testid="edit-cancel" onClick={onCancel}>{t("Cancel")}</button>
          <button type="button" className="primary-btn" data-testid="edit-save" disabled={!valid || busy} onClick={save}>
            {busy && <Loader2 size={16} className="spin" />}{t("Save")}</button>
        </div>
      </div>
    </div>
  );
}
